/**
 * Premium Selection save/load — real Quote backend
 * (`/v2/submissions/{id}/quote/`, PUT/GET).
 *
 * Model (jun-19): every total on the section is BACKEND-COMPUTED. The FE
 * sends the manual inputs + criteria/dates/notes on Save (PUT); the
 * backend recomputes the premium walk and returns the totals on GET. So
 * the load returns BOTH the editable form state AND the derived totals,
 * read straight off the quote wire — no facilities/loss-runs summing here
 * anymore. The wire ↔ form/derived mapping (incl. %↔fraction and the
 * criteria id↔enum bridge) lives in `api/adapters/quote.ts`.
 */
import { defaultPremiumSelectionInputs } from "../../domain/pricing";
import type { PremiumSelectionDerived } from "../../domain/pricing";
import type { MiscFee } from "../../types/misc-fee";
import { wireToMiscFee } from "../../types/misc-fee";
import { ApiError, getJsonReal, sendJsonReal } from "../client";
import { derivedFromWireQuote, fromWireQuote, toWireQuote } from "../adapters/quote";
import { applyQuoteDecFallback, readDecFields, writeDecFields } from "../dec-fields-local";
import type { QuoteFormState, SubmissionQuoteGetWire, ToWireQuoteOptions } from "../adapters/quote";

/** Save payload = the section's form state (manual inputs + criteria +
 *  dates/notes). Totals are backend-computed, so they're not sent. */
export type PremiumSelectionPayload = QuoteFormState;

/** Load result = editable form state + backend-computed totals. */
export interface PremiumSelectionLoad {
    form: QuoteFormState;
    derived: PremiumSelectionDerived;
    /**
     * When InsCipher last ran (OR-1440). Null until backend PR #327 ships the
     * column — dev omits the key today, so this is null everywhere and the
     * provenance tooltip renders without its timestamp line.
     */
    taxCalculatedAt: string | null;
    /** Backend's provenance flag for the persisted tax/fee rate (OR-1440
     *  follow-up). `null` when the backend hasn't shipped the column yet —
     *  callers must fall back to their own session-local heuristic, not
     *  assume `false`. */
    surplusLinesTaxManuallySet: boolean | null;
    surplusLinesFeeManuallySet: boolean | null;
    /** Stamping Fee counterpart of the two flags above — same contract. */
    stampingFeeManuallySet: boolean | null;
    /**
     * Who owns the Tech/RM Fee (backend PR #371): `false` = the backend derives it
     * from the covered facility count, `true` = a person set it and the
     * schedule no longer moves it. `null` when the backend hasn't shipped the
     * column — callers must treat that as unknown, not as derived.
     */
    techRmFeeManuallySet: boolean | null;
    /** Home state InsCipher resolved and filed the tax/fee against (OR-1440
     *  follow-up), persisted on the quote. `null` when there's no calculation
     *  on record yet, or the quote predates this column — callers must fall
     *  back to inference in that case, not assume "no taxable-fee state". */
    taxState: string | null;
    /** Underwriter-defined fee lines embedded in the quote (OR-1494). Empty
     *  when the backend omits the key. */
    miscFees: MiscFee[];
}

function quoteBase(submissionId: string): string {
    return `/v2/submissions/${encodeURIComponent(submissionId)}/quote/`;
}

/** PUT the quote. No-op without an id (create flow hasn't saved yet).
 *  `options` forwards to `toWireQuote` — see `ToWireQuoteOptions` for why the
 *  surplus-lines rates are withheld by default (OR-1440). */
export async function savePremiumSelection(
    submissionId: string | undefined,
    payload: PremiumSelectionPayload,
    options?: ToWireQuoteOptions,
): Promise<void> {
    if (!submissionId) return;
    await sendJsonReal(quoteBase(submissionId), "PUT", toWireQuote(payload, options));
    // OR-1455 — keep the dec sub-limits locally until PR #326 deploys, so a
    // reload doesn't drop what the UW just typed. No-op behind the flag;
    // remove with `dec-fields-local`.
    writeDecFields(submissionId, {
        fireDamagePerClaimLimit: payload.inputs.fireDamagePerClaimLimit,
        fireDamageAggregateLimit: payload.inputs.fireDamageAggregateLimit,
        glEachClaimDeductible: payload.inputs.glEachClaimDeductible,
        medicalExpenseLimit: payload.inputs.medicalExpenseLimit,
        evacuationLimit: payload.inputs.evacuationLimit,
        evacuationAggregateLimit: payload.inputs.evacuationAggregateLimit,
        evacuationDeductible: payload.inputs.evacuationDeductible,
    });
}

/** GET the quote as form state + derived totals. `null` when none exists
 *  yet (404) → the page keeps its defaults and shows "—" for every total. */
export async function getPremiumSelection(submissionId: string | undefined): Promise<PremiumSelectionLoad | null> {
    if (!submissionId) return null;
    try {
        const wire = await getJsonReal<SubmissionQuoteGetWire>(quoteBase(submissionId));
        const form = fromWireQuote(wire, defaultPremiumSelectionInputs());
        // OR-1455 — stand in for the dec sub-limits the backend doesn't send
        // yet. Applied per field and only where the wire was null, so a real
        // value always wins. Remove with `dec-fields-local`.
        applyQuoteDecFallback(form.inputs, wire, readDecFields(submissionId));
        return {
            form,
            derived: derivedFromWireQuote(wire),
            taxCalculatedAt: wire.tax_calculated_at ?? null,
            surplusLinesTaxManuallySet: wire.surplus_lines_tax_manually_set ?? null,
            surplusLinesFeeManuallySet: wire.surplus_lines_fee_manually_set ?? null,
            stampingFeeManuallySet: wire.stamping_fee_rate_manually_set ?? null,
            techRmFeeManuallySet: wire.tech_rm_fee_manually_set ?? null,
            taxState: wire.tax_state ?? null,
            miscFees: (wire.misc_fees ?? []).map(wireToMiscFee),
        };
    } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
    }
}

/**
 * Surplus-lines tax + fee as calculated by InsCipher (OR-1440).
 *
 * Both rates are decimal FRACTIONS (0.0485 = 4.85%), and either can be `null`:
 * a state whose surplus-lines charge is a flat dollar amount rather than a
 * percentage has an amount but no rate.
 *
 * ⚠️ The amounts are NOT `rate × premium`. InsCipher returns the computed
 * amount and the rate independently and they can disagree by cents (backend
 * PR #321 observed 84174.00 against a rate implying 84174.22). What it filed is
 * the amount, so the UI must display THIS value and never recompute it.
 */
export interface SubmissionTaxResultWire {
    surplus_lines_tax_rate: string | null;
    surplus_lines_tax_amount: string;
    surplus_lines_fee_rate: string | null;
    surplus_lines_fee_amount: string;
    /** Stamping Fee counterpart of the two pairs above — same contract. */
    stamping_fee_rate: string | null;
    stamping_fee_amount: string;
    /** State the taxes were calculated for (home state of the risk). */
    state: string;
}

/**
 * Trigger a live InsCipher surplus-lines tax calculation and persist it onto
 * the quote. **User-initiated only** — InsCipher's quota is roughly 1,000
 * requests per YEAR, which is the whole reason this isn't automatic.
 *
 * The backend guards the quota itself: it fingerprints the assembled request,
 * so pressing the button again with unchanged inputs returns the persisted
 * values without an outbound call, and an advisory lock serialises concurrent
 * presses. The FE still disables the button while in flight (design), but that
 * is UX, not the safeguard.
 *
 * Throws `ApiError` with the backend's own message in `.message`, which is
 * written to be shown as-is:
 *   409 — submission is not in "Indications Sent"
 *   422 — missing selected premium / effective date / resolvable home state,
 *         or a Kentucky risk without address, city and ZIP
 *   502 — InsCipher itself failed
 */
export async function calculateSurplusLinesTax(submissionId: string): Promise<SubmissionTaxResultWire> {
    return sendJsonReal<SubmissionTaxResultWire>(`${quoteBase(submissionId)}tax-calculation`, "POST");
}

/** Lean fetch of the quote's `updated_at` (ISO string; "" when there is no
 *  quote yet or the call fails). The Files section stamps the generated
 *  Quote/Binder documents with it (OR-1324) — kept separate from
 *  `getPremiumSelection` on purpose, so the stamp never depends on the full
 *  form/derived adapters (a wire-shape drift that breaks hydration must not
 *  also blank the Files stamps). */
export async function getQuoteUpdatedAt(submissionId: string | undefined): Promise<string> {
    if (!submissionId) return "";
    try {
        const wire = await getJsonReal<Pick<SubmissionQuoteGetWire, "updated_at">>(quoteBase(submissionId));
        return wire.updated_at ?? "";
    } catch {
        return "";
    }
}
